import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  Box,
  Chip,
  useTheme,
  tableCellClasses,
  styled
} from '@mui/material';
import {
  ArrowDownward as DecreaseIcon,
  ArrowUpward as IncreaseIcon,
  SwapHoriz as TransferIcon,
  Settings as AdjustmentIcon
} from '@mui/icons-material';
import { InventoryTransaction, TransactionType } from '../../../types/inventory';
import { formatDateTimeNumeric } from '../../../utils/dateUtils';

interface InventoryMovementTableProps {
  data: InventoryTransaction[];
}

// تخصيص خلايا الجدول
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#f5f5f5',
    color: theme.palette.text.primary,
    fontWeight: 'bold',
    fontSize: '0.95rem',
    padding: '16px 8px',
    textAlign: 'right'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '0.9rem',
    padding: '12px 8px',
    textAlign: 'right'
  }
}));

// تخصيص صفوف الجدول
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // تأثيرات التحويم
  '&:hover': {
    backgroundColor: 'rgba(48, 195, 176, 0.08)',
    transition: 'background-color 0.2s ease'
  },
  // المباعدة بين الصفوف
  '& > td': {
    borderBottom: '1px solid rgba(224, 224, 224, 0.5)'
  }
}));

// مكون جدول حركة المخزون
const InventoryMovementTable: React.FC<InventoryMovementTableProps> = ({ data }) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // معالجة تغيير الصفحة
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // معالجة تغيير عدد الصفوف في الصفحة
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // الحصول على نوع الحركة بالعربية
  const getTransactionTypeText = (type: string) => {
    switch (type) {
      case 'addition':
        return 'إضافة';
      case 'subtraction':
        return 'خصم';
      case 'transfer':
        return 'تحويل';
      case 'transfer_in':
        return 'وارد تحويل';
      case 'transfer_out':
        return 'صادر تحويل';
      case 'adjustment':
        return 'تعديل';
      default:
        return type;
    }
  };

  // الحصول على أيقونة نوع الحركة
  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'addition':
        return <IncreaseIcon fontSize="small" color="success" />;
      case 'subtraction':
        return <DecreaseIcon fontSize="small" color="error" />;
      case 'transfer':
      case 'transfer_in':
      case 'transfer_out':
        return <TransferIcon fontSize="small" color="primary" />;
      case 'adjustment':
        return <AdjustmentIcon fontSize="small" color="warning" />;
      default:
        return null;
    }
  };

  // الحصول على لون شريحة نوع الحركة
  const getTransactionTypeColor = (type: string): string => {
    switch (type) {
      case 'addition':
        return '#4caf50';
      case 'subtraction':
        return '#f44336';
      case 'transfer':
      case 'transfer_in':
      case 'transfer_out':
        return '#2196f3';
      case 'adjustment':
        return '#ff9800';
      default:
        return '#9e9e9e';
    }
  };

  // عرض البيانات المفلترة حسب الصفحة
  const visibleRows = rowsPerPage === -1
    ? data
    : data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
        <Table stickyHeader aria-label="جدول حركة المخزون">
          <TableHead>
            <TableRow>
              <StyledTableCell>التاريخ</StyledTableCell>
              <StyledTableCell>نوع الحركة</StyledTableCell>
              <StyledTableCell>المنتج والمتغير</StyledTableCell>
              <StyledTableCell>خصائص المتغير</StyledTableCell>
              <StyledTableCell>الكمية</StyledTableCell>
              <StyledTableCell>الكمية الجديدة</StyledTableCell>
              <StyledTableCell>المستودع</StyledTableCell>
              <StyledTableCell>باركود المتغير</StyledTableCell>
              <StyledTableCell>المرجع</StyledTableCell>
              <StyledTableCell>ملاحظات</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length > 0 ? (
              visibleRows.map((row) => (
                <StyledTableRow key={row._id}>
                  <StyledTableCell>{formatDateTimeNumeric(row.transactionDate)}</StyledTableCell>
                  <StyledTableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getTransactionIcon(row.type)}
                      {getTransactionTypeText(row.type)}
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography component="span" sx={{ fontWeight: 'bold' }}>
                      {row.productName}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    {(() => {
                      // التحقق من وجود خصائص المتغير
                      if (!row.variantAttributes || typeof row.variantAttributes !== 'object') {
                        return <Typography variant="body2" color="text.secondary">-</Typography>;
                      }
                      
                      // تصفية الخصائص لإزالة الباركود
                      const attributes = Object.entries(row.variantAttributes)
                        .filter(([key]) =>
                          key !== 'barcode' &&
                          key !== 'باركود' &&
                          key.toLowerCase() !== 'barcode'
                        )
                        .map(([key, value]) => value)
                        .filter(value => value && value.toString().trim() !== '');
                      
                      // إذا لم توجد خصائص بعد التصفية
                      if (attributes.length === 0) {
                        return <Typography variant="body2" color="text.secondary">-</Typography>;
                      }
                      
                      // عرض الخصائص
                      return (
                        <Typography variant="body2" color="text.secondary">
                          {attributes.join(' - ')}
                        </Typography>
                      );
                    })()}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 'bold',
                          color: row.type === 'addition' ? '#2e7d32' :
                                 row.type === 'subtraction' ? '#c62828' : 'inherit'
                        }}
                      >
                        {row.type === 'addition' ? '+' : 
                         row.type === 'subtraction' ? '-' : ''}
                        {row.quantity} {row.unitName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({row.previousQuantity} → {row.newQuantity})
                      </Typography>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ color: '#30c3b0' }}
                    >
                      {row.newQuantity} {row.unitName}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>{row.warehouseName}</StyledTableCell>
                  <StyledTableCell>
                    {/* عرض الباركود مباشرة من حقل الباركود الجديد */}
                    {row.barcode || (
                      // البحث في خصائص المتغير كاحتياط
                      (() => {
                        if (row.variantAttributes && typeof row.variantAttributes === 'object') {
                          // البحث عن الباركود باللغة الإنجليزية أو العربية
                          if (row.variantAttributes.barcode) {
                            return row.variantAttributes.barcode;
                          }
                          if (row.variantAttributes.باركود) {
                            return row.variantAttributes.باركود;
                          }
                          
                          // البحث في كل خصائص المتغير
                          const barcodeEntry = Object.entries(row.variantAttributes)
                            .find(([key]) => key.toLowerCase() === 'barcode' || key === 'باركود');
                          
                          if (barcodeEntry) {
                            return barcodeEntry[1];
                          }
                        }
                        return '';
                      })()
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    {row.referenceNumber ? (
                      <Chip
                        label={`${row.referenceModel === 'Invoice' ? 'فاتورة' : 'مستند'}: ${row.referenceNumber}`}
                        size="small"
                        sx={{
                          backgroundColor: `${getTransactionTypeColor(row.type)}20`,
                          color: getTransactionTypeColor(row.type),
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          border: `1px solid ${getTransactionTypeColor(row.type)}40`
                        }}
                      />
                    ) : '-'}
                  </StyledTableCell>
                  <StyledTableCell>
                    {(() => {
                      if (!row.notes) return '-';
                      
                      // استخراج اسم صفحة الفاتورة من الملاحظات
                      const invoicePageMatch = row.notes.match(/صفحة الفاتورة:\s*([^\n,]+)/);
                      if (invoicePageMatch) {
                        return invoicePageMatch[1].trim();
                      }
                      
                      // إذا كانت الملاحظات تحتوي على رموز أو أكواد، نتجاهلها
                      const cleanNotes = row.notes
                        .replace(/\{[^}]*\}/g, '') // إزالة الأقواس المعقوفة ومحتواها
                        .replace(/\[[^\]]*\]/g, '') // إزالة الأقواس المربعة ومحتواها
                        .replace(/[<>]/g, '') // إزالة علامات أكبر من وأصغر من
                        .replace(/^\s*[\w-]+:\s*/gm, '') // إزالة المفاتيح مثل "key: value"
                        .trim();
                      
                      return cleanNotes || '-';
                    })()}
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">لا توجد بيانات متاحة</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, { label: 'الكل', value: -1 }]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="عدد الصفوف:"
        labelDisplayedRows={
          ({ from, to, count }) => {
            if (rowsPerPage === -1) {
              return `عرض الكل (${count})`;
            }
            return `${from}-${to} من ${count !== -1 ? count : `أكثر من ${to}`}`;
          }
        }
      />
    </Box>
  );
};

export default InventoryMovementTable;